<template>
	<div>
		<div v-if="buyoutLoadError">
			<p>{{ buyoutLoadError }}</p>
		</div>
		<device-finder id="deviceFinder" v-if="!buyoutLoadError">
			<template slot="title">
				<div class="col-12 buyout__buyoutTitle">
					{{ $t('buyout.deviceFinder.searchDevice.title') }}
				</div>
			</template>
		</device-finder>
		<device-state
			id="deviceState"
			v-if="selectedColor"
			:deviceFormsOffset="deviceFormsOffset"
		/>
		<template
			v-if="selectedQuality || (appraisal && defaultQualityConditions.length)"
		>
			<slot
				name="pre__deviceForms"
				:isOnlineBuyout="isOnlineBuyout"
				:preSaleInfo="preSaleInfo"
			></slot>
			<div id="deviceForms" class="row deviceForms">
				<div class="col">
					<appraisal-form id="appraisalForm" />
				</div>
				<div class="col" v-if="isOnlineBuyout">
					<contract-form :reset-button-enabled="resetButtonEnabled" :is-bank-account="isBankAccount" id="contractForm" />
				</div>
			</div>
			<div
				id="canNotBeBuyedOut"
				class="row buyout__error"
				v-if="canNotBeBuyout"
			>
				<div class="col text-center text-error">
					<div class="buyout__errorText">
						{{ $t('buyout.bad_quality') }}
					</div>
					<div class="buyout__errorTitle">
						{{ $t('buyout.can_not_be_buyout') }}
					</div>
				</div>
			</div>
			<template v-if="!canNotBeBuyout && selectedQuality">
				<div
					class="row buttons__block"
					v-if="!buyoutState || (buyoutState && buyoutState.is_editable)"
				>
					<div class="col" v-if="!userBuyoutCode">
						<button @click="toggleBuyoutType(false)" class="buyout__btn">
							{{ $t('buyout.send_info_email') }}
						</button>
					</div>
					<div class="col" v-if="!isOnlineBuyout">
						<button @click="toggleBuyoutType(true)" class="buyout__btn">
							{{ $t('buyout.sell_online') }}
						</button>
					</div>
					<div class="col" v-if="isOnlineBuyout">
						<button
							v-scroll-to="{
								el: '#deviceSummary',
								offset: -200,
								duration: 500
							}"
							class="buyout__btn"
						>
							{{ $t('buyout.continue') }}
						</button>
					</div>
				</div>
				<device-summary id="deviceSummary" />
			</template>

			<template v-if="!canNotBeBuyout && preSaleInfo">
				<div id="buyoutInfoEmail" class="buyoutInfoEmail row">
					<div class="row">
						<div class="col">
							<p>
								{{ $t('buyout.send_info_email_label') }}
							</p>
						</div>
					</div>
					<div class="row">
						<div class="col">
							<v-form v-model="isEmailValid">
								<v-text-field
									label="Email"
									v-model="preSaleEmailInfo"
									:rules="email_rules"
									:disabled="Boolean(userBuyoutCode)"
									outlined
								></v-text-field>
							</v-form>
						</div>
					</div>
					<div class="row" v-if="!userBuyoutCode">
						<div class="col">
							<button
								:disabled="!isEmailValid"
								@click="appraisalSend"
								class="buyout__btn send__btn"
							>
								{{ $t('buyout.send') }}
							</button>
						</div>
					</div>
				</div>
			</template>
			<template v-if="isOnlineBuyout && !canNotBeBuyout && !displayThankYou">
				<v-checkbox v-model="privacyPolicyCheck" class="confirm_agreement"/>
				<p v-html="privacyPolicyText" class="confirm_agreement"></p>
				<button
					id="send"
					class="buyout__btn send__btn"
					:disabled="!privacyPolicyCheck"
					@click="appraisalSend"
				>
					{{ $t('buyout.send') }}
				</button>
			</template>
		</template>
		<slot name="thankYouDialog">
			<div
				id="thankYouDialog"
				class="row buyout__order thankYouDialog"
				v-if="displayThankYou && (preSaleInfo || isOnlineBuyout)"
			>
				<div class="col text-center">
					<div class="green__wrapper info__wrapper">
						<div class="buyout__orderTitle" v-html="orderTitle"></div>
						<div class="buyout__orderText" v-html="orderText"></div>
					</div>
				</div>
			</div>
		</slot>
	</div>
</template>

<script>
import DeviceFinder from '../DeviceFinder/App';
import ContractForm from '../ContractForm/App';
import AppraisalForm from '../AppraisalForm/App';
import DeviceState from '../DeviceState/App';
import DeviceSummary from '../DeviceSummary/App';

import { config } from '../../../config/buyout';
import { sleep } from '../../../../js/buyout';
import SanitizeMixin from '../../../mixins/buyout/sanitize';
import {mapMutations, mapActions, mapGetters, mapState} from 'vuex';
import {VUEX_BUYOUT} from "../../../store/buyout";
import { GETTER_TYPES } from '../../../store/buyout/_getter-types';
import { MUTATION_TYPES } from '../../../store/buyout/_mutation-types';
import { ACTION_TYPES } from '../../../store/buyout/_action-types';

export default {
	name: 'Buyout',
	mixins: [SanitizeMixin],
	components: {
		DeviceFinder,
		ContractForm,
		AppraisalForm,
		DeviceState,
		DeviceSummary
	},
	props: {
		deviceFormsOffset: {
			type: Number,
			default: 0,
			required: false
		},
		isBankAccount: {
			type:Boolean,
			default: true
		},
		resetButtonEnabled: {
			type: Boolean,
			default: true
		}
	},
	async created() {
		if (!this.qualityTypes.length) await this[ACTION_TYPES.LOAD_QUALITY_TYPES];
	},
	data() {
		return {
			privacyPolicyCheck: false,
			isEmailValid: true,
			email_rules: [
				value => !!value || this.$t('buyout.validation.not_null'),
				value => {
					const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
					return (
						pattern.test(value) || this.$t('buyout.validation.invalid_email')
					);
				}
			]
		}
	},
	computed: {
		...mapState(VUEX_BUYOUT.NAMESPACE, [
			'qualityTypes',
			'displayThankYou',
			'userBuyoutCode',
			'buyoutState',
			'selectedQuality',
			'appraisal',
			'defaultQualityConditions',
			'selectedColor',
			'canNotBeBuyout',
			'buyoutLoadError',
		]),
		...mapGetters(VUEX_BUYOUT.NAMESPACE, [
			GETTER_TYPES.IS_PRE_SALE_INFO,
			GETTER_TYPES.IS_BUYOUT_ONLINE,
			GETTER_TYPES.PRESALE_INFO_EMAIL,
			GETTER_TYPES.GET_BUYOUT_PAYLOAD
		]),
		preSaleInfo: {
			get() {
				return this[GETTER_TYPES.IS_PRE_SALE_INFO];
			},
			set(v) {
				this[MUTATION_TYPES.SET_IS_PRE_SALE_INFO](v);
			}
		},
		isOnlineBuyout: {
			get() {
				return this[GETTER_TYPES.IS_BUYOUT_ONLINE];
			},
			set(v) {
				this[MUTATION_TYPES.SET_IS_BUYOUT_ONLINE](v);
			}
		},
		preSaleEmailInfo: {
			get() {
				return this[GETTER_TYPES.PRESALE_INFO_EMAIL];
			},
			set(v) {
				this[MUTATION_TYPES.SET_PRE_SALE_INFO_EMAIL](v);
			}
		},
		privacyPolicyText() {
			const link = `<a href="${config.privacy_policy.link}">${config.privacy_policy.link_title}</a>`;
			return (
				config.privacy_policy &&
				this.$sanitize(config.privacy_policy.text.replace('{X}', link))
			);
		},
		storesLink: () => config.stores_link,
		orderTitle() {
			return this.$sanitize(
				this.$t(
					`buyout.thankYou.${
						this.preSaleInfo ? 'preSale' : 'onlineBuyout'
					}.title`,
					[this.userBuyoutCode]
				)
			);
		},
		orderText() {
			return this.$sanitize(
				this.$t(
					`buyout.thankYou.${this.preSaleInfo ? 'preSale' : 'onlineBuyout'}.${
						this.preSaleInfo && this.storesLink ? 'text_w_link' : 'text'
					}`,
					this.preSaleInfo && this.storesLink ? [this.storesLink] : []
				)
			);
		}
	},
	methods: {
		...mapActions(VUEX_BUYOUT.NAMESPACE,[
			ACTION_TYPES.SEND_APPRAISAL,
			ACTION_TYPES.LOAD_QUALITY_TYPES,
		]),
		...mapMutations(VUEX_BUYOUT.NAMESPACE,[
			MUTATION_TYPES.SET_IS_PRE_SALE_INFO,
			MUTATION_TYPES.SET_IS_BUYOUT_ONLINE,
			MUTATION_TYPES.SET_PRE_SALE_INFO_EMAIL,
			MUTATION_TYPES.SET_IS_APPRAISAL_EDITABLE,
		]),
		async appraisalSend() {
			return await this[ACTION_TYPES.SEND_APPRAISAL]({payload: this[GETTER_TYPES.GET_BUYOUT_PAYLOAD]});
		},
		async toggleBuyoutType(onlineBuyout) {
			this.preSaleInfo = !onlineBuyout;
			this.isOnlineBuyout = onlineBuyout;

			if (config.scrollToElements) {
				await sleep(150);
				if (this.preSaleInfo) {
					this.$scrollTo('#deviceSummary', { offset: -50, duration: 500 });
				}

				if (this.isOnlineBuyout) {
					this[MUTATION_TYPES.SET_IS_APPRAISAL_EDITABLE](true);
					this.$scrollTo('#deviceForms', {
						offset: this.deviceFormsOffset,
						duration: 500
					});
				}
			}
		}
	},
	watch: {
		async canNotBeBuyout(newValue) {
			if (newValue && config.scrollToElements) {
				await sleep(100);
				this.$scrollTo('#canNotBeBuyedOut', 500);
			}
		}
	},
}
</script>
